import { Skeleton } from '../../components/Skeleton';
import FunnelStageRow from './FunnelStageRow';
import type { ProvenanceKind, StageProvenance } from './FunnelStageProvenance';
import type { StageLiveness } from './FunnelStageLiveness';

export type { ProvenanceKind, StageProvenance, StageLiveness };
export type FunnelCategory = 'by_design' | 'fixable' | 'waiting';
export type FunnelView = 'survivors' | 'dropped';

export interface FunnelStage {
  key: string;
  label: string;
  count: number;
  drop: number;
  drop_label: string | null;
  category: FunnelCategory | null;
  provenance?: StageProvenance;
  liveness?: StageLiveness;
}

export interface PredictionFunnelProps {
  stages: FunnelStage[];
  total: number;
  asOf: string;
  loading?: boolean;
  error?: string | null;
  onStageClick: (stage: FunnelStage, view: FunnelView) => void;
  selectedStage?: { stage: FunnelStage; view: FunnelView } | null;
}

const categoryLegend: {
  key: FunnelCategory | 'derived';
  label: string;
  description: string;
}[] = [
  {
    key: 'by_design',
    label: 'By design',
    description: 'Loss is inherent — no work exists',
  },
  {
    key: 'fixable',
    label: 'Fixable',
    description: 'Real backlog — needs engineering',
  },
  {
    key: 'waiting',
    label: 'Waiting on time',
    description: 'Resolves automatically on its date',
  },
  {
    key: 'derived',
    label: 'Derived',
    description: 'Computed from an earlier stage — no job, so no liveness',
  },
];

function formatAsOf(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const now = Date.now();
  const diffMs = now - date.getTime();
  if (diffMs < 60_000) return 'just now';
  if (diffMs < 60 * 60_000) return `${Math.round(diffMs / 60_000)}m ago`;
  if (diffMs < 24 * 60 * 60_000) return `${Math.round(diffMs / (60 * 60_000))}h ago`;
  return date.toLocaleString();
}

export default function PredictionFunnel({
  stages,
  total,
  asOf,
  loading,
  error,
  onStageClick,
  selectedStage,
}: PredictionFunnelProps) {
  const fixableTotal = stages
    .filter((s) => s.category === 'fixable')
    .reduce((sum, s) => sum + s.drop, 0);
  const lostTotal = total - (stages[stages.length - 1]?.count ?? 0);

  return (
    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Prediction attrition
          </h3>
          {stages.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {total.toLocaleString()} extracted predictions become{' '}
              {(stages[stages.length - 1]?.count ?? 0).toLocaleString()} grades.{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {lostTotal.toLocaleString()} lost
              </span>
              ; the actionable backlog is{' '}
              <span className="font-medium text-red-600 dark:text-red-400">
                {fixableTotal.toLocaleString()} fixable
              </span>
              .
            </p>
          )}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
          as of {formatAsOf(asOf)}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-3">
        {categoryLegend.map((c) => (
          <div key={c.key} className="flex items-center gap-1.5">
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${
                c.key === 'by_design'
                  ? 'bg-gray-400'
                  : c.key === 'fixable'
                  ? 'bg-red-500'
                  : c.key === 'derived'
                  ? 'bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-600'
                  : 'bg-blue-500'
              }`}
            />
            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
              {c.label}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              — {c.description}
            </span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2 py-1">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/30 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      ) : stages.length === 0 ? (
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6 text-center text-sm text-gray-500 dark:text-gray-400">
          No funnel data available.
        </div>
      ) : (
        <div className="space-y-1">
          {stages.map((stage) => (
            <FunnelStageRow
              key={stage.key}
              stage={stage}
              total={total}
              selected={selectedStage?.stage.key === stage.key}
              onClickSurvivors={() => onStageClick(stage, 'survivors')}
              onClickDrop={() => onStageClick(stage, 'dropped')}
            />
          ))}
        </div>
      )}
    </div>
  );
}
